/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Autocomplete,
  Chip,
  TextField,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import {
  SelectInputComponent,
  CountryCodeWithPhoneNumber,
} from "../../../../common/components";
import { shareLeadCaptureForms } from "../../../../api/lead-capture";
import { getCampaigns } from "../../../../api/campaign";
import CreateLeadCapturePopupConfirmationQRCode from "./create-lead-capture-popup-confirmation-qr-code";
import CloseIcon from "../../../../assets/icons/closeIcon.svg";
import "./create-lead-capture-popup-confirmation.scss";
import {
  handleDownload,
  handlePrint,
} from "../../../surveys/qr-code/qr-code-list.constant";
import { emailValidationRegex } from "../../../../constants/regexStrings";

const CreateLeadCapturePopupConfirmation = (props: any) => {
  const {
    isShowConfirmationPopup,
    setIsShowConfirmationPopup,
    createLeadCaptureId,
    formLink,
    setAlertOpen,
    setAlertMessage,
    setAlertType,
    setLoading,
    handleClosePreview,
    setCreateLeadCaptureInitialValues = () => {},
    qrCodeUrl,
    formName,
  } = props;

  const [campaignsData, setCampaignsData] = useState<any>([]);
  const [emailsData, setEmailsDataData] = useState<any>([]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params: any = {
        activeCampaigns: true,
      };
      const response = await getCampaigns(params, "Lead Capture");
      if (response?.data?.data?.length > 0) {
        const campaigns = response?.data?.data?.map((item: any) => ({
          value: item?.name,
          labels: item?.id,
        }));
        setCampaignsData(campaigns);
      } else {
        setCampaignsData([]);
      }
      setLoading(false);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch campaigns",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // setTimeout(() => {
    //   if(setCreateLeadCaptureInitialValues){
    //     setCreateLeadCaptureInitialValues(null);
    //   }
    // }, 9000);
  }, []);

  const validationSchema = () =>
    Yup.object().shape({
      emailAddress: Yup.array()
        .of(Yup.string().email())
        .when("selectedOption", (val: any) => {
          return val[0] === "emailAddress"
            ? Yup.array()
                .of(
                  Yup.string().email((dataArray: any) => {
                    return dataArray?.originalPath ===
                      `emailAddress[${emailsData?.length - 1}]`
                      ? "One or more email addresses are invalid. Please correct them before Sharing."
                      : "";
                  }),
                )
                .min(1, "Email address is required")
            : Yup.array().notRequired();
        }),
      assignToCampaign: Yup.array()
        .of(Yup.string())
        .when("selectedOption", (val: any) => {
          return val[0] === "assignToCampaign"
            ? Yup.array().min(1, "Assign to Campaign is required")
            : Yup.array().notRequired();
        }),
    });

  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const selectedCampaign = values?.assignToCampaign?.map(
        (item: any) =>
          campaignsData?.find((campaign: any) => campaign?.value === item)
            ?.labels,
      );
      const payload = {
        emailIds: values?.emailAddress,
        campaignIds: selectedCampaign?.filter((item: any) => item),
        shareLeadCaptureForm: selectedCampaign?.length > 0 ? true : false,
      };
      const response = await shareLeadCaptureForms(payload, values?.id);
      setLoading(false);
      setIsShowConfirmationPopup(false);
      setAlertMessage(response?.data?.data?.message);
      setAlertType("success");
      setAlertOpen(true);
      if (handleClosePreview) {
        handleClosePreview();
      }
    } catch (error: any) {
      setLoading(false);
      if (error?.response?.data?.message === "Validation failed") {
        const errors = error?.response?.data?.errors;

        const hasEmailError = errors?.some((err: any) =>
          Object.keys(err)[0].includes("emailIds"),
        );
        setAlertMessage(
          hasEmailError
            ? "One or more email addresses are invalid. Please check and try again."
            : "Some fields contain invalid values.",
        );
      } else {
        setAlertMessage(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to share Lead Capture form",
        );
      }
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  return (
    <>
      <Dialog
        open={isShowConfirmationPopup}
        // onClose={() => {setIsShowConfirmationPopup(false)
        //   setCreateLeadCaptureInitialValues(null)}
        // }
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={`create-lead-capture-popup-confirmation`}
      >
        <DialogTitle id="alert-dialog-title">
          <div className="header-wrapper">
            <div className="image-wrapper">
              <div className="popup-title">Share your Lead Capture form</div>
              <img
                src={CloseIcon}
                className="close-icon"
                onClick={() => {
                  setIsShowConfirmationPopup(false);
                  setCreateLeadCaptureInitialValues(null);
                }}
              />
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="create-lead-capture-popup-confirmation-description">
            <Formik
              initialValues={{
                selectedOption: "emailAddress",
                emailAddress: [],
                sms: "",
                embedLink: formLink || "",
                assignToCampaign: [],
                id: createLeadCaptureId,
                showCopied: false,
                type: "email address",
              }}
              validationSchema={validationSchema}
              validateOnChange={true}
              enableReinitialize={true}
              onSubmit={(values, { resetForm }) =>
                handleSave(values, resetForm)
              }
            >
              {({
                values,
                setFieldValue,
                touched,
                setFieldTouched,
                handleSubmit,
                validateForm,
              }) => (
                <Form>
                  <div className="cancel-popup-sub-text">
                    You can now save the designed lead capture form or share it
                    with your target audience via {values?.type}.
                  </div>
                  <div className="create-lead-capture-popup-confirmation-description-checkbox">
                    <FormControl>
                      <RadioGroup row>
                        <FormControlLabel
                          value="Email address"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "emailAddress");
                                setFieldValue("sms", "");
                                setFieldValue("embedLink", "");
                                setFieldValue("assignToCampaign", []);
                                setFieldValue("emailAddress", []);
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "email address");
                              }}
                              checked={
                                values?.selectedOption === "emailAddress"
                              }
                            />
                          }
                          label="Email address"
                        />
                        {/* <FormControlLabel
                          value="SMS"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "sms");
                                setFieldValue("emailAddress", []);
                                setFieldValue("embedLink", "");
                                setFieldValue("assignToCampaign", []);
                                setFieldValue("sms", "");
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "sms");
                              }}
                              checked={values?.selectedOption === "sms"}
                            />
                          }
                          label="SMS"
                        /> */}
                        <FormControlLabel
                          value="Embed Link"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "embedLink");
                                setFieldValue("emailAddress", []);
                                setFieldValue("sms", "");
                                setFieldValue("assignToCampaign", []);
                                setFieldValue("embedLink", formLink || "");
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "embed link ");
                              }}
                              checked={values?.selectedOption === "embedLink"}
                            />
                          }
                          label="Embed Link"
                        />
                        <FormControlLabel
                          value="Assign to Campaign"
                          control={<Radio />}
                          label="Assign to Campaign"
                          onChange={() => {
                            setFieldValue("selectedOption", "assignToCampaign");
                            setFieldValue("sms", "");
                            setFieldValue("embedLink", "");
                            setFieldValue("assignToCampaign", []);
                            setFieldValue("emailAddress", []);
                            setFieldValue("showCopied", false);
                            setFieldValue("type", "assign to campaign");
                          }}
                          checked={
                            values?.selectedOption === "assignToCampaign"
                          }
                        />
                        <FormControlLabel
                          value="QR Code"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "QR Code");
                                setFieldValue("sms", "");
                                setFieldValue("embedLink", "");
                                setFieldValue("assignToCampaign", []);
                                setFieldValue("emailAddress", []);
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "QR Code");
                                setFieldValue("emailAddress", []);
                              }}
                              checked={values?.selectedOption === "QR Code"}
                            />
                          }
                          label="QR Code"
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                  <div className="create-lead-capture-popup-confirmation-description-checkbox-input">
                    {values?.selectedOption === "emailAddress" ? (
                      <div className="form-group">
                        <Autocomplete
                          multiple
                          id="tags-filled"
                          options={[]}
                          defaultValue={[]}
                          freeSolo
                          onChange={(e: any, value: any) => {
                            setEmailsDataData(value);
                            setTimeout(() => {
                              setFieldValue("emailAddress", value);
                            }, 500);
                          }}
                          renderTags={(value: any[], getTagProps: any) =>
                            value.map((option: any, index: any) => {
                              return (
                                <Chip
                                  key={index}
                                  label={option}
                                  color="primary"
                                  {...getTagProps({ index })}
                                />
                              );
                            })
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              placeholder="Add a receiver by pressing enter after its dotName or address"
                            />
                          )}
                        />
                        {touched?.emailAddress && (
                          <ErrorMessage
                            name={"emailAddress"}
                            component="div"
                            className="error-message"
                          />
                        )}
                      </div>
                    ) : values?.selectedOption === "sms" ? (
                      <div className="form-group">
                        <CountryCodeWithPhoneNumber
                          fieldName="sms"
                          placeholder={values?.sms ? "" : "Enter Phone Number"}
                          onChange={(
                            value: string,
                            country: any,
                            one: any,
                            two: any,
                          ) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object",
                              );
                              return;
                            }

                            if (`+${countryCode}` == two) {
                              setFieldValue("sms", null);
                            } else {
                              if (two?.includes(`+${countryCode}`)) {
                                setFieldValue("sms", two);
                              } else {
                                setFieldValue("sms", countryCode);
                              }
                            }
                          }}
                          onBlur={() => {
                            setFieldTouched("sms", true);
                          }}
                          value={values?.sms || "+1"}
                          onKeyDown={(value: any) => {
                            if (
                              value.location == 0 &&
                              value.key == "Backspace"
                            ) {
                              setFieldValue(
                                "sms",
                                values?.sms?.substring(
                                  0,
                                  values?.sms?.length - 1,
                                ),
                              );
                            }
                            if (value.location == 0 && value.key == "Delete") {
                              setFieldValue(
                                "sms",
                                values?.sms?.substring(
                                  0,
                                  values?.sms?.length - 1,
                                ),
                              );
                            }
                          }}
                          defaultCountry="us"
                          optional={true}
                          // countryCodeEditable
                        />
                        {touched?.sms && touched?.sms && (
                          <ErrorMessage
                            name={"sms"}
                            component="div"
                            className="error-message"
                          />
                        )}
                      </div>
                    ) : values?.selectedOption === "embedLink" ? (
                      <FormControl
                        fullWidth
                        sx={{ m: 1 }}
                        className="embed-link-input"
                      >
                        <OutlinedInput
                          type={"text"}
                          disabled={false}
                          value={values.embedLink}
                          endAdornment={
                            <InputAdornment position="end">
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    values.embedLink,
                                  );
                                  setFieldValue("showCopied", true);
                                  setTimeout(() => {
                                    setFieldValue("showCopied", false);
                                  }, 2000);
                                }}
                              >
                                {values.showCopied ? "copied" : "copy"}
                              </button>
                            </InputAdornment>
                          }
                        />
                      </FormControl>
                    ) : values?.selectedOption === "assignToCampaign" ? (
                      <div className="form-group">
                        <SelectInputComponent
                          fieldName="assignToCampaign"
                          placeholder="Select Campaign"
                          optional={false}
                          disabled={false}
                          multiple={true}
                          options={campaignsData}
                          value={values?.assignToCampaign}
                          onChange={(e: any) =>
                            setFieldValue("assignToCampaign", e)
                          }
                        />
                        {touched?.assignToCampaign && (
                          <ErrorMessage
                            name={"assignToCampaign"}
                            component="div"
                            className="error-message"
                          />
                        )}
                      </div>
                    ) : values?.selectedOption === "QR Code" ? (
                      <div className="form-group">
                        <CreateLeadCapturePopupConfirmationQRCode
                          setLoading={setLoading}
                          url={qrCodeUrl}
                        />
                      </div>
                    ) : null}
                  </div>
                  <DialogActions
                    className="confirmation-button-wrapper"
                    style={{ marginTop: "20px" }}
                  >
                    <button
                      onClick={() => {
                        setIsShowConfirmationPopup(false);
                        setCreateLeadCaptureInitialValues(null);
                      }}
                      type="button"
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <div className="save-action-btn">
                      {values?.selectedOption === "QR Code" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              // Handle print functionality
                              handlePrint(qrCodeUrl, formName);
                            }}
                            className="action-btn"
                            style={{ marginRight: "10px" }}
                          >
                            Print
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleDownload(qrCodeUrl);
                            }}
                            className="action-btn"
                          >
                            Download
                          </button>
                        </>
                      ) : values?.selectedOption !== "embedLink" ? (
                        <button
                          type={"button"}
                          onClick={() => {
                            validateForm();
                            handleSubmit();
                          }}
                          autoFocus
                          className="action-btn"
                        >
                          Share
                        </button>
                      ) : null}
                    </div>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateLeadCapturePopupConfirmation;
